import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { ContextMenu, type ContextMenuState } from "@/components/ui/ContextMenu";
import { Checkbox, SelectInput, TextareaInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ConfirmActionDialog } from "@/pages/montageplanung/ConfirmActionDialog";
import { DocumentSaveStatus } from "@/pages/dokumente/DocumentSaveStatus";
import type { DokumentSaveState } from "@/pages/dokumente/useDokumentAutoSave";
import {
  BESCHICHTUNG_ART_OPTIONS,
  cloneStueckliste,
  createStueckliste,
  createStuecklisteRevision,
  deleteStueckliste,
  duplicateStuecklistePositionInList,
  finalizeStueckliste,
  formatBeschichtungDisplay,
  getZuschnittBadgeParts,
  isWerkzeugPosition,
  listAuftragStuecklisten,
  listStuecklisteTkVorschlaege,
  normalizeStuecklistePack,
  pickLatestStuecklisten,
  positionStatusLabel,
  reorderStuecklistePositions,
  searchStuecklisten,
  setStuecklistePack,
  stuecklisteDisplayNumber,
  stuecklistePdfUrl,
  tkVorschlagToPosition,
  updateStueckliste,
  type BeschichtungArt,
  type BeschichtungFinish,
  type BeschichtungGrundiertTon,
  type Stueckliste,
  type StuecklistePack,
  type StuecklistePosition,
  type StuecklisteSearchResult,
  type StuecklisteTkVorschlag,
  type StuecklisteUpsertPayload,
} from "@/lib/stuecklisten";
import {
  getOrder,
  getOrderedMaterials,
  type OrderedMaterialLine,
} from "@/lib/projectOverview";
import {
  patchProjectExecutionClass,
  projectExecutionClassBadgeClass,
  type ProjectExecutionClass,
} from "@/lib/projects";
import {
  buildStuecklisteEditorSnapshot,
  buildStuecklisteSnapshotFromItem,
  editorHasUnsavedStuecklisteChanges,
} from "@/projectOverview/stuecklisteEditorSnapshot";
import { useStuecklisteAutoSave } from "@/projectOverview/useStuecklisteAutoSave";
import { StuecklistePositionDialog } from "@/projectOverview/StuecklistePositionDialog";
import { StuecklistePublicViewDialog } from "@/projectOverview/StuecklistePublicViewDialog";
import { StuecklisteWerkzeugDialog } from "@/projectOverview/StuecklisteWerkzeugDialog";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";
import { userCanManageMontageunterlagen } from "@/projectOverview/permissions";
import type { CurrentUser } from "@/lib/auth";

type BeschichtungSnapshot = Pick<
  StuecklisteUpsertPayload,
  | "beschichtung_art"
  | "beschichtung_grundiert_ton"
  | "beschichtung_finish"
  | "beschichtung_ral"
>;

const EMPTY_BESCHICHTUNG: BeschichtungSnapshot = {
  beschichtung_art: null,
  beschichtung_grundiert_ton: null,
  beschichtung_finish: null,
  beschichtung_ral: null,
};

function orderedMaterialToVorschlag(line: OrderedMaterialLine): StuecklisteTkVorschlag | null {
  const rawId = line.order_line_id ?? line.id ?? line.value;
  const orderLineId = Number(rawId);
  if (!Number.isFinite(orderLineId) || orderLineId <= 0) return null;
  const cancelled = Boolean(line.order_cancelled);
  const mengeRaw = line.menge ?? line.default_menge;
  let menge: number | null = null;
  if (mengeRaw != null && mengeRaw !== "") {
    const parsed = typeof mengeRaw === "number" ? mengeRaw : parseFloat(String(mengeRaw).replace(",", "."));
    menge = Number.isFinite(parsed) ? parsed : null;
  }
  return {
    order_line_id: orderLineId,
    order_id: Number(line.order_id) || 0,
    bestellnummer: line.bestellnummer || null,
    text: line.text || null,
    menge,
    einheit: line.einheit || null,
    hero_nr: line.hero_nr || null,
    order_cancelled: cancelled,
    tk_status: cancelled ? "storniert" : "offen",
    tk_status_label: cancelled ? "Storniert" : "Offen",
    already_linked: false,
  };
}

function mergeTkVorschlaege(
  fromOrders: StuecklisteTkVorschlag[],
  fromApi: StuecklisteTkVorschlag[],
): StuecklisteTkVorschlag[] {
  const byId = new Map<number, StuecklisteTkVorschlag>();
  for (const row of fromOrders) byId.set(row.order_line_id, row);
  for (const row of fromApi) {
    const prev = byId.get(row.order_line_id);
    if (!prev) {
      byId.set(row.order_line_id, row);
      continue;
    }
    byId.set(row.order_line_id, {
      ...prev,
      ...row,
      text: row.text || prev.text,
      bestellnummer: row.bestellnummer || prev.bestellnummer,
      menge: row.menge ?? prev.menge,
      einheit: row.einheit || prev.einheit,
      hero_nr: row.hero_nr || prev.hero_nr,
      tk_status: row.tk_status || prev.tk_status,
      tk_status_label: row.tk_status_label || prev.tk_status_label,
    });
  }
  return [...byId.values()].sort((a, b) => {
    const bn = String(a.bestellnummer || "").localeCompare(String(b.bestellnummer || ""), "de");
    if (bn !== 0) return bn;
    return a.order_line_id - b.order_line_id;
  });
}

type PositionListKind = "standard" | "werkzeug";

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function resolveDropIndex(index: number, before: boolean): number {
  return before ? index : index + 1;
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(status: string | null | undefined): string {
  return String(status || "").toLowerCase() === "fertig" ? "Fertig" : "Entwurf";
}

function positionStatusBadgeClass(status: string | null | undefined): string {
  switch (String(status || "offen").toLowerCase()) {
    case "lager":
      return "bg-status-blue/15 text-status-blue";
    case "nicht_vorhanden":
      return "bg-status-red/15 text-status-red";
    case "bestellt":
      return "bg-status-orange/15 text-status-orange";
    case "kommissioniert":
      return "bg-status-green/15 text-status-green";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

export function StuecklisteEditorPanel({
  orderId,
  user,
  groups,
  pack = "montage",
  initialStuecklisteId = null,
  autoStartCreate = false,
  onChanged,
  onEditingIdChange,
}: {
  orderId: number;
  user: CurrentUser | null;
  groups: string[];
  /** Herkunft: Werkstatt-Stücklisten erscheinen auch in den Montageunterlagen. */
  pack?: StuecklistePack;
  initialStuecklisteId?: number | null;
  /** Beim Öffnen direkt den Anlege-Modus starten. */
  autoStartCreate?: boolean;
  onChanged?: () => void;
  /** Nach Create/Hydrate: Parent kann Focus-ID setzen (Remount-sicher). */
  onEditingIdChange?: (id: number | null) => void;
}) {
  const documentPack = normalizeStuecklistePack(pack);
  const canManage = userCanManageMontageunterlagen(user, groups);
  const [items, setItems] = useState<Stueckliste[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const autoCreateStartedRef = useRef(false);
  const hydratedInitialIdRef = useRef<number | null>(null);
  const lastGoodBeschichtungRef = useRef<BeschichtungSnapshot>({ ...EMPTY_BESCHICHTUNG });
  const ignoreListReloadUntilRef = useRef(0);
  const hasUnsavedChangesRef = useRef(false);
  const saveStateRef = useRef<DokumentSaveState>("idle");
  const positionDialogOpenRef = useRef(false);
  const werkzeugDialogOpenRef = useRef(false);

  const [datum, setDatum] = useState(todayIso());
  const [bauteil, setBauteil] = useState("");
  const [beschichtungArt, setBeschichtungArt] = useState<BeschichtungArt | "">("");
  const [beschichtungGrundiertTon, setBeschichtungGrundiertTon] = useState<
    BeschichtungGrundiertTon | ""
  >("");
  const [beschichtungFinish, setBeschichtungFinish] = useState<BeschichtungFinish | "">("");
  const [beschichtungRal, setBeschichtungRal] = useState("");
  const [executionClass, setExecutionClass] = useState<ProjectExecutionClass | "">("");
  const [savingExc, setSavingExc] = useState(false);
  const [positionen, setPositionen] = useState<StuecklistePosition[]>([]);
  const [werkzeugPositionen, setWerkzeugPositionen] = useState<StuecklistePosition[]>([]);
  const [notizen, setNotizen] = useState("");
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [werkzeugDialogOpen, setWerkzeugDialogOpen] = useState(false);
  const [editPositionIndex, setEditPositionIndex] = useState<number | null>(null);
  const [editWerkzeugIndex, setEditWerkzeugIndex] = useState<number | null>(null);
  const [tkVorschlaege, setTkVorschlaege] = useState<StuecklisteTkVorschlag[]>([]);
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
  const [publicViewUrl, setPublicViewUrl] = useState<string | null>(null);
  const [dragState, setDragState] = useState<{
    kind: PositionListKind;
    from: number;
  } | null>(null);
  const [dropState, setDropState] = useState<{
    kind: PositionListKind;
    index: number;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [templateQuery, setTemplateQuery] = useState("");
  const [templateResults, setTemplateResults] = useState<StuecklisteSearchResult[]>([]);
  const [templateSearching, setTemplateSearching] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<StuecklisteSearchResult | null>(null);
  const [keepOrderLinks, setKeepOrderLinks] = useState(false);
  const [cloningTemplate, setCloningTemplate] = useState(false);
  const templateSearchSeqRef = useRef(0);

  const visibleItems = useMemo(
    () => (items ? pickLatestStuecklisten(items) : null),
    [items],
  );
  const activeItem = useMemo(
    () => (editingId != null ? items?.find((row) => row.id === editingId) ?? null : null),
    [editingId, items],
  );
  const isFinal = String(activeItem?.status || "").toLowerCase() === "fertig";
  const editorOpen = creating || editingId != null;

  const linkedOrderLineIds = useMemo(() => {
    const ids = new Set<number>();
    for (const pos of positionen) {
      if (pos.order_line_id != null) ids.add(pos.order_line_id);
    }
    return ids;
  }, [positionen]);

  // Alle nicht stornierten Bestellzeilen vorschlagen. Dieselbe Zeile darf mehrfach
  // übernommen werden (z. B. 60 m Rohr → viele Zuschnittpositionen).
  const availableTkVorschlaege = useMemo(
    () => tkVorschlaege.filter((row) => !row.order_cancelled),
    [tkVorschlaege],
  );

  const availableTkGrouped = useMemo(() => {
    const groups = new Map<string, StuecklisteTkVorschlag[]>();
    for (const row of availableTkVorschlaege) {
      const key = String(row.bestellnummer || "Ohne Bestellnummer");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    }
    return [...groups.entries()];
  }, [availableTkVorschlaege]);

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const [list, orderedMaterials, apiVorschlaege, order] = await Promise.all([
        listAuftragStuecklisten(orderId),
        // Gleiche Quelle wie „Bestellungen & Artikel“ in der Projektübersicht.
        getOrderedMaterials(orderId).catch(() => [] as OrderedMaterialLine[]),
        listStuecklisteTkVorschlaege(orderId).catch(() => [] as StuecklisteTkVorschlag[]),
        getOrder(orderId).catch(() => null),
      ]);
      setItems(Array.isArray(list) ? list : []);
      const fromOrders = (Array.isArray(orderedMaterials) ? orderedMaterials : [])
        .map(orderedMaterialToVorschlag)
        .filter((row): row is StuecklisteTkVorschlag => row != null);
      setTkVorschlaege(
        mergeTkVorschlaege(fromOrders, Array.isArray(apiVorschlaege) ? apiVorschlaege : []),
      );
      const excFromList = list?.[0]?.execution_class;
      const excRaw = String(order?.execution_class || excFromList || "")
        .trim()
        .toUpperCase();
      setExecutionClass(excRaw === "EXC1" || excRaw === "EXC2" ? excRaw : "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stücklisten konnten nicht geladen werden.");
      setItems([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const rememberBeschichtung = useCallback((item: BeschichtungSnapshot) => {
    lastGoodBeschichtungRef.current = {
      beschichtung_art: item.beschichtung_art || null,
      beschichtung_grundiert_ton: item.beschichtung_grundiert_ton || null,
      beschichtung_finish: item.beschichtung_finish || null,
      beschichtung_ral: item.beschichtung_ral || null,
    };
  }, []);

  const fillFromItem = useCallback((item: Stueckliste) => {
    setDatum(item.datum || todayIso());
    setBauteil(item.bauteil || "");
    setBeschichtungArt((item.beschichtung_art as BeschichtungArt) || "");
    setBeschichtungGrundiertTon(
      (item.beschichtung_grundiert_ton as BeschichtungGrundiertTon) || "",
    );
    setBeschichtungFinish((item.beschichtung_finish as BeschichtungFinish) || "");
    setBeschichtungRal(item.beschichtung_ral || "");
    setNotizen(item.notizen || "");
    const all = item.positionen || [];
    const standard = all.filter((p) => !isWerkzeugPosition(p));
    const werkzeug = all.filter((p) => isWerkzeugPosition(p));
    setPositionen(
      standard.map((p, idx) => ({
        ...p,
        position_kind: "standard",
        position_nr: p.position_nr || idx + 1,
        zuschnitt_erforderlich: Boolean(p.zuschnitt_erforderlich),
        ist_montagematerial: Boolean(p.ist_montagematerial),
      })),
    );
    setWerkzeugPositionen(
      werkzeug.map((p, idx) => ({
        ...p,
        position_kind: "werkzeug",
        position_nr: p.position_nr || idx + 1,
        zuschnitt_erforderlich: false,
        ist_montagematerial: false,
      })),
    );
    rememberBeschichtung(item);
    setSavedSnapshot(buildStuecklisteSnapshotFromItem(item));
  }, [rememberBeschichtung]);

  useEffect(() => {
    if (!initialStuecklisteId || !items?.length) return;
    if (hydratedInitialIdRef.current === initialStuecklisteId) return;
    const match = items.find((row) => row.id === initialStuecklisteId);
    if (!match) return;
    hydratedInitialIdRef.current = initialStuecklisteId;
    setCreating(false);
    setEditingId(match.id);
    fillFromItem(match);
    onEditingIdChange?.(match.id);
    if (normalizeStuecklistePack(match.pack) !== documentPack && canManage) {
      void setStuecklistePack(match.id, documentPack)
        .then((synced) => {
          fillFromItem(synced);
          return reload({ silent: true });
        })
        .then(() => onChanged?.())
        .catch(() => {
          /* Pack-Sync ist best effort; Speichern setzt pack erneut. */
        });
    }
  }, [
    initialStuecklisteId,
    items,
    fillFromItem,
    documentPack,
    canManage,
    reload,
    onChanged,
    onEditingIdChange,
  ]);

  function resetTemplatePicker() {
    setTemplateQuery("");
    setTemplateResults([]);
    setTemplateSearching(false);
    setSelectedTemplate(null);
    setKeepOrderLinks(false);
    setCloningTemplate(false);
    templateSearchSeqRef.current += 1;
  }

  function resetEditor() {
    setCreating(false);
    setEditingId(null);
    setDatum(todayIso());
    setBauteil("");
    setBeschichtungArt("");
    setBeschichtungGrundiertTon("");
    setBeschichtungFinish("");
    setBeschichtungRal("");
    setNotizen("");
    setPositionen([]);
    setWerkzeugPositionen([]);
    setEditPositionIndex(null);
    setEditWerkzeugIndex(null);
    setPositionDialogOpen(false);
    setWerkzeugDialogOpen(false);
    resetTemplatePicker();
    lastGoodBeschichtungRef.current = { ...EMPTY_BESCHICHTUNG };
    setSavedSnapshot(null);
    hydratedInitialIdRef.current = null;
    onEditingIdChange?.(null);
  }

  const beginCreateForm = useCallback(() => {
    setEditingId(null);
    setDatum(todayIso());
    setBauteil("");
    setBeschichtungArt("");
    setBeschichtungGrundiertTon("");
    setBeschichtungFinish("");
    setBeschichtungRal("");
    setEditPositionIndex(null);
    setEditWerkzeugIndex(null);
    setPositionDialogOpen(false);
    setWerkzeugDialogOpen(false);
    setCreating(true);
    setNotizen("");
    setPositionen([]);
    setWerkzeugPositionen([]);
    setTemplateQuery("");
    setTemplateResults([]);
    setTemplateSearching(false);
    setSelectedTemplate(null);
    setKeepOrderLinks(false);
    setCloningTemplate(false);
    templateSearchSeqRef.current += 1;
    lastGoodBeschichtungRef.current = { ...EMPTY_BESCHICHTUNG };
    setSavedSnapshot(
      buildStuecklisteEditorSnapshot({
        pack: documentPack,
        datum: todayIso(),
        bauteil: null,
        beschichtung_art: null,
        beschichtung_grundiert_ton: null,
        beschichtung_finish: null,
        beschichtung_ral: null,
        notizen: null,
        positionen: [],
      }),
    );
  }, [documentPack]);

  const canKeepOrderLinks =
    selectedTemplate != null && Number(selectedTemplate.auftrag_id) === Number(orderId);
  const showTemplatePicker = creating && editingId == null && canManage;

  useEffect(() => {
    if (!showTemplatePicker) return;
    const q = templateQuery.trim();
    if (q.length < 1) {
      setTemplateResults([]);
      setTemplateSearching(false);
      return;
    }
    const seq = ++templateSearchSeqRef.current;
    setTemplateSearching(true);
    const handle = window.setTimeout(() => {
      void searchStuecklisten(q)
        .then((rows) => {
          if (seq !== templateSearchSeqRef.current) return;
          setTemplateResults(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (seq !== templateSearchSeqRef.current) return;
          setTemplateResults([]);
        })
        .finally(() => {
          if (seq !== templateSearchSeqRef.current) return;
          setTemplateSearching(false);
        });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [templateQuery, showTemplatePicker]);

  useEffect(() => {
    if (!selectedTemplate) return;
    if (Number(selectedTemplate.auftrag_id) !== Number(orderId)) {
      setKeepOrderLinks(false);
    }
  }, [selectedTemplate, orderId]);

  useEffect(() => {
    if (!autoStartCreate || autoCreateStartedRef.current || initialStuecklisteId != null) return;
    if (!canManage || items === null) return;
    autoCreateStartedRef.current = true;
    beginCreateForm();
  }, [autoStartCreate, canManage, initialStuecklisteId, items, beginCreateForm]);

  useEffect(() => {
    function onExternalChange() {
      if (Date.now() < ignoreListReloadUntilRef.current) return;
      if (hasUnsavedChangesRef.current) return;
      if (saveStateRef.current === "pending" || saveStateRef.current === "saving") return;
      if (positionDialogOpenRef.current || werkzeugDialogOpenRef.current) return;
      void reload({ silent: true });
    }
    window.addEventListener("montageunterlagen_changed", onExternalChange);
    window.addEventListener("auftraege_changed", onExternalChange);
    return () => {
      window.removeEventListener("montageunterlagen_changed", onExternalChange);
      window.removeEventListener("auftraege_changed", onExternalChange);
    };
  }, [reload]);

  async function handleExecutionClassChange(next: ProjectExecutionClass | "") {
    if (!canManage) return;
    setSavingExc(true);
    setError(null);
    try {
      const updated = await patchProjectExecutionClass(orderId, next || null);
      const exc = String(updated.execution_class || "").trim().toUpperCase();
      setExecutionClass(exc === "EXC1" || exc === "EXC2" ? exc : "");
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Execution Class konnte nicht gespeichert werden.");
    } finally {
      setSavingExc(false);
    }
  }

  function startCreate() {
    resetEditor();
    beginCreateForm();
  }

  function validateBeschichtung(): string | null {
    if (beschichtungArt === "grundiert" && !beschichtungGrundiertTon) {
      return "Bei Grundierung bitte hell oder dunkel wählen.";
    }
    if (
      (beschichtungArt === "gepulvert" || beschichtungArt === "lackiert") &&
      !beschichtungRal.trim()
    ) {
      return "Bitte RAL-Ton für die Beschichtung angeben.";
    }
    if (beschichtungArt === "verzinkt" && beschichtungFinish && !beschichtungRal.trim()) {
      return "Bitte RAL-Ton für die Nachbehandlung angeben.";
    }
    return null;
  }

  const buildPayload = useCallback((): StuecklisteUpsertPayload => {
    const standard = positionen.map((p, idx) => ({
      ...p,
      position_kind: "standard" as const,
      position_nr: idx + 1,
    }));
    const werkzeug = werkzeugPositionen.map((p, idx) => ({
      ...p,
      position_kind: "werkzeug" as const,
      position_nr: idx + 1,
    }));
    return {
      pack: documentPack,
      datum: datum || null,
      bauteil: bauteil.trim() || null,
      beschichtung_art: beschichtungArt || null,
      beschichtung_grundiert_ton:
        beschichtungArt === "grundiert" ? beschichtungGrundiertTon || null : null,
      beschichtung_finish:
        beschichtungArt === "verzinkt" ? beschichtungFinish || null : null,
      beschichtung_ral:
        beschichtungArt === "gepulvert" ||
        beschichtungArt === "lackiert" ||
        (beschichtungArt === "verzinkt" && !!beschichtungFinish)
          ? beschichtungRal.trim() || null
          : null,
      notizen: notizen.trim() || null,
      positionen: [...standard, ...werkzeug],
    };
  }, [
    documentPack,
    datum,
    bauteil,
    beschichtungArt,
    beschichtungGrundiertTon,
    beschichtungFinish,
    beschichtungRal,
    notizen,
    positionen,
    werkzeugPositionen,
  ]);

  const buildAutosavePayload = useCallback((): StuecklisteUpsertPayload => {
    const payload = buildPayload();
    // Unvollständige Beschichtung nicht an die API schicken – letzten gültigen Stand behalten.
    if (
      (beschichtungArt === "grundiert" && !beschichtungGrundiertTon) ||
      ((beschichtungArt === "gepulvert" || beschichtungArt === "lackiert") &&
        !beschichtungRal.trim()) ||
      (beschichtungArt === "verzinkt" && !!beschichtungFinish && !beschichtungRal.trim())
    ) {
      return { ...payload, ...lastGoodBeschichtungRef.current };
    }
    return payload;
  }, [
    buildPayload,
    beschichtungArt,
    beschichtungGrundiertTon,
    beschichtungFinish,
    beschichtungRal,
  ]);

  const currentSnapshot = useMemo(
    () => buildStuecklisteEditorSnapshot(buildAutosavePayload()),
    [buildAutosavePayload],
  );

  const editorEditable = Boolean(canManage && editorOpen && !isFinal);

  const hasUnsavedChanges = useMemo(
    () => editorHasUnsavedStuecklisteChanges(buildAutosavePayload(), savedSnapshot, editorEditable),
    [buildAutosavePayload, savedSnapshot, editorEditable],
  );

  hasUnsavedChangesRef.current = hasUnsavedChanges;
  positionDialogOpenRef.current = positionDialogOpen;
  werkzeugDialogOpenRef.current = werkzeugDialogOpen;

  const handleAutoSaved = useCallback(
    (
      saved: Stueckliste,
      meta: { created: boolean; payload: StuecklisteUpsertPayload },
    ) => {
      ignoreListReloadUntilRef.current = Date.now() + 1500;
      setCreating(false);
      setEditingId(saved.id);
      // Verhindert erneutes fillFromItem wenn Parent die Focus-ID nachzieht.
      hydratedInitialIdRef.current = saved.id;
      rememberBeschichtung(meta.payload);
      // Snapshot = tatsächlich gespeicherter Payload (nicht Server-Extras) → kein Save-Loop.
      setSavedSnapshot(buildStuecklisteEditorSnapshot(meta.payload));
      setItems((prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((row) => row.id === saved.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [saved, ...prev];
      });
      if (meta.created) {
        onEditingIdChange?.(saved.id);
        // Parent-Tabs aktualisieren – nicht bei jedem Tastenanschlag.
        onChanged?.();
      }
    },
    [onChanged, onEditingIdChange, rememberBeschichtung],
  );

  const { saveState, flushSave, retrySave, lastSavedAt, saveError } = useStuecklisteAutoSave({
    orderId,
    editingId,
    creating,
    dirty: hasUnsavedChanges,
    formKey: currentSnapshot,
    editable: editorEditable,
    buildPayload: buildAutosavePayload,
    onSaved: handleAutoSaved,
  });

  saveStateRef.current = saveState;

  const editorEditableRef = useRef(editorEditable);
  editorEditableRef.current = editorEditable;
  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;

  // Beim Schließen/Unmount noch ausstehenden Entwurf flushen.
  useEffect(() => {
    return () => {
      if (hasUnsavedChangesRef.current && editorEditableRef.current) {
        void flushSaveRef.current();
      }
    };
  }, []);

  async function ensureItemPack(item: Stueckliste): Promise<Stueckliste> {
    if (normalizeStuecklistePack(item.pack) === documentPack) return item;
    return setStuecklistePack(item.id, documentPack);
  }

  async function startEdit(item: Stueckliste) {
    setCreating(false);
    setSaving(true);
    setError(null);
    try {
      const synced = await ensureItemPack(item);
      setEditingId(synced.id);
      hydratedInitialIdRef.current = synced.id;
      fillFromItem(synced);
      onEditingIdChange?.(synced.id);
      if (synced !== item) {
        await reload({ silent: true });
        onChanged?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stückliste konnte nicht geöffnet werden.");
      setEditingId(item.id);
      fillFromItem(item);
      onEditingIdChange?.(item.id);
    } finally {
      setSaving(false);
    }
  }

  async function startRevision(item: Stueckliste) {
    if (!canManage || String(item.status).toLowerCase() !== "fertig") return;
    setSaving(true);
    setError(null);
    try {
      ignoreListReloadUntilRef.current = Date.now() + 1500;
      await ensureItemPack(item);
      const revision = await createStuecklisteRevision(item.id);
      const syncedRevision = await ensureItemPack(revision);
      await reload({ silent: true });
      setCreating(false);
      setEditingId(syncedRevision.id);
      hydratedInitialIdRef.current = syncedRevision.id;
      fillFromItem(syncedRevision);
      onEditingIdChange?.(syncedRevision.id);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Revision konnte nicht erzeugt werden.");
    } finally {
      setSaving(false);
    }
  }

  async function applyTemplateClone() {
    if (!canManage || !selectedTemplate) return;
    const keepLinks = keepOrderLinks && Number(selectedTemplate.auftrag_id) === Number(orderId);
    setCloningTemplate(true);
    setSaving(true);
    setError(null);
    try {
      ignoreListReloadUntilRef.current = Date.now() + 1500;
      const cloned = await cloneStueckliste(selectedTemplate.id, {
        target_auftrag_id: orderId,
        keep_order_links: keepLinks,
        pack: documentPack,
      });
      const synced = await ensureItemPack(cloned);
      await reload({ silent: true });
      resetTemplatePicker();
      setCreating(false);
      setEditingId(synced.id);
      hydratedInitialIdRef.current = synced.id;
      fillFromItem(synced);
      onEditingIdChange?.(synced.id);
      onChanged?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Stückliste konnte nicht aus Vorlage übernommen werden.",
      );
    } finally {
      setCloningTemplate(false);
      setSaving(false);
    }
  }

  async function requestFinalize() {
    if (!canManage) return;
    const beschichtungError = validateBeschichtung();
    if (beschichtungError) {
      setError(beschichtungError);
      return;
    }
    if (!positionen.length) {
      setError("Bitte mindestens eine Position erfassen.");
      return;
    }
    await flushSave();
    setFinalizeConfirmOpen(true);
  }

  async function handleFinalizeConfirmed() {
    if (!canManage) return;
    setFinalizeConfirmOpen(false);
    setSaving(true);
    setError(null);
    try {
      await flushSave();
      let id = editingId;
      const payload = buildPayload();
      const beschichtungError = validateBeschichtung();
      if (beschichtungError) {
        setError(beschichtungError);
        return;
      }
      if (creating || id == null) {
        const created = await createStueckliste(orderId, payload);
        id = created.id;
      } else if (!isFinal) {
        await updateStueckliste(id, payload);
      }
      ignoreListReloadUntilRef.current = Date.now() + 1500;
      const finalized = await finalizeStueckliste(id);
      await reload({ silent: true });
      setCreating(false);
      setEditingId(finalized.id);
      fillFromItem(finalized);
      onEditingIdChange?.(finalized.id);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fertigstellen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: Stueckliste) {
    if (!canManage || String(item.status).toLowerCase() === "fertig") return;
    if (!window.confirm("Entwurf wirklich löschen?")) return;
    setSaving(true);
    setError(null);
    try {
      await deleteStueckliste(item.id);
      if (editingId === item.id) resetEditor();
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function openCreatePosition() {
    setEditPositionIndex(null);
    setPositionDialogOpen(true);
  }

  function openEditPosition(index: number) {
    setEditPositionIndex(index);
    setPositionDialogOpen(true);
  }

  function openCreateWerkzeug() {
    setEditWerkzeugIndex(null);
    setWerkzeugDialogOpen(true);
  }

  function openEditWerkzeug(index: number) {
    setEditWerkzeugIndex(index);
    setWerkzeugDialogOpen(true);
  }

  function savePosition(position: StuecklistePosition, mode: "create" | "edit") {
    setPositionen((prev) => {
      if (mode === "edit" && editPositionIndex != null) {
        return prev.map((row, idx) =>
          idx === editPositionIndex
            ? { ...position, position_kind: "standard", position_nr: idx + 1 }
            : row,
        );
      }
      return [
        ...prev,
        { ...position, position_kind: "standard", position_nr: prev.length + 1 },
      ];
    });
    setPositionDialogOpen(false);
    setEditPositionIndex(null);
  }

  function saveWerkzeugPosition(position: StuecklistePosition) {
    setWerkzeugPositionen((prev) => {
      if (editWerkzeugIndex != null) {
        return prev.map((row, idx) =>
          idx === editWerkzeugIndex
            ? { ...position, position_kind: "werkzeug", position_nr: idx + 1 }
            : row,
        );
      }
      return [
        ...prev,
        { ...position, position_kind: "werkzeug", position_nr: prev.length + 1 },
      ];
    });
    setWerkzeugDialogOpen(false);
    setEditWerkzeugIndex(null);
  }

  function removePosition(index: number) {
    setPositionen((prev) =>
      prev.filter((_, idx) => idx !== index).map((row, idx) => ({ ...row, position_nr: idx + 1 })),
    );
  }

  function removeWerkzeugPosition(index: number) {
    setWerkzeugPositionen((prev) =>
      prev.filter((_, idx) => idx !== index).map((row, idx) => ({ ...row, position_nr: idx + 1 })),
    );
  }

  function movePosition(kind: PositionListKind, from: number, to: number) {
    if (kind === "standard") {
      setPositionen((prev) => reorderStuecklistePositions(prev, from, to));
    } else {
      setWerkzeugPositionen((prev) => reorderStuecklistePositions(prev, from, to));
    }
  }

  function duplicatePosition(kind: PositionListKind, index: number) {
    if (kind === "standard") {
      setPositionen((prev) => duplicateStuecklistePositionInList(prev, index));
    } else {
      setWerkzeugPositionen((prev) => duplicateStuecklistePositionInList(prev, index));
    }
  }

  function openPositionContextMenu(
    kind: PositionListKind,
    index: number,
    e: React.MouseEvent,
  ) {
    if (isFinal || !canManage) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          id: "duplicate",
          label: "Position duplizieren",
          onClick: () => duplicatePosition(kind, index),
        },
        {
          id: "edit",
          label: "Bearbeiten",
          onClick: () =>
            kind === "standard" ? openEditPosition(index) : openEditWerkzeug(index),
        },
        {
          id: "remove",
          label: "Entfernen",
          destructive: true,
          onClick: () =>
            kind === "standard" ? removePosition(index) : removeWerkzeugPosition(index),
        },
      ],
    });
  }

  function clearDrag() {
    setDragState(null);
    setDropState(null);
  }

  function adoptTkVorschlag(vorschlag: StuecklisteTkVorschlag) {
    setPositionen((prev) => [
      ...prev,
      tkVorschlagToPosition(vorschlag, prev.length + 1),
    ]);
  }

  function adoptAllTkVorschlaege() {
    // Nur noch nicht verknüpfte Zeilen – Mehrfachübernahme einer Zeile bewusst
    // über „Übernehmen“ oder Duplizieren (Zuschnitte).
    setPositionen((prev) => {
      const existing = new Set(
        prev.map((p) => p.order_line_id).filter((id): id is number => id != null),
      );
      const additions = availableTkVorschlaege
        .filter((row) => !existing.has(row.order_line_id))
        .map((row, idx) => tkVorschlagToPosition(row, prev.length + idx + 1));
      return [...prev, ...additions];
    });
  }

  return (
    <div className="space-y-4">
      {loading && items === null ? (
        <div className="flex items-center py-6">
          <Spinner />
          <span className="ml-2 text-sm text-label-secondary">Lade Stücklisten …</span>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-ios border border-separator bg-fill-quaternary p-3">
            <div className="min-w-[10rem] flex-1">
              <SelectInput
                label="Execution Class (EXC)"
                value={executionClass}
                onChange={(e) =>
                  void handleExecutionClassChange(
                    e.target.value as ProjectExecutionClass | "",
                  )
                }
                disabled={!canManage || savingExc}
                hint="Gilt für alle Stücklisten dieses Projekts."
              >
                <option value="">— bitte wählen —</option>
                <option value="EXC1">EXC1</option>
                <option value="EXC2">EXC2</option>
              </SelectInput>
            </div>
            {executionClass ? (
              <span className={projectExecutionClassBadgeClass(executionClass)}>
                {executionClass}
              </span>
            ) : null}
          </div>

          {visibleItems && visibleItems.length > 0 ? (
            <ul className="space-y-2">
              {visibleItems.map((item) => {
                const missingCount = (item.positionen || []).filter(
                  (p) => String(p.position_status || "").toLowerCase() === "nicht_vorhanden",
                ).length;
                const isItemFinal = String(item.status).toLowerCase() === "fertig";
                return (
                <li
                  key={item.id}
                  className="rounded-ios border border-separator bg-fill-quaternary px-3 py-2.5"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                          {statusLabel(item.status)}
                        </span>
                        <span className="truncate text-sm font-medium text-label">
                          {item.dokument_nummer
                            ? stuecklisteDisplayNumber(item)
                            : "Stückliste (Entwurf)"}
                        </span>
                        {missingCount > 0 ? (
                          <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[11px] font-medium text-status-red">
                            {missingCount} nicht vorhanden
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-label-tertiary">
                        {item.bauteil ? `${item.bauteil} · ` : ""}
                        {item.created_by_display || item.created_by_login || "Unbekannt"}
                        {item.created_at ? ` · ${fmtDateTime(item.created_at)}` : ""}
                        {` · ${item.positionen?.length || 0} Position(en)`}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {item.public_view_url ? (
                          <button
                            type="button"
                            onClick={() => setPublicViewUrl(item.public_view_url || null)}
                            className="text-xs text-accent hover:underline"
                          >
                            Digitale Ansicht
                          </button>
                        ) : null}
                        {item.sharepoint_web_url ? (
                          <a
                            href={item.sharepoint_web_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-accent hover:underline"
                          >
                            In SharePoint öffnen
                          </a>
                        ) : item.status === "fertig" ? (
                          <a
                            href={stuecklistePdfUrl(item.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-accent hover:underline"
                          >
                            PDF öffnen
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {isItemFinal ? (
                        <>
                          {canManage ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => void startRevision(item)}
                              disabled={saving}
                            >
                              Bearbeiten
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(item)}
                            disabled={saving}
                          >
                            Anzeigen
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(item)}
                            disabled={saving}
                          >
                            Bearbeiten
                          </Button>
                          {canManage ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => void handleDelete(item)}
                              disabled={saving}
                            >
                              Löschen
                            </Button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-label-tertiary">Noch keine Stücklisten vorhanden.</p>
          )}

          {canManage && !editorOpen ? (
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={startCreate}>
                Neue Stückliste
              </Button>
            </div>
          ) : null}

          {editorOpen ? (
            <div className="space-y-3 rounded-ios border border-separator bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-label">
                  {isFinal
                    ? `Stückliste ${activeItem ? stuecklisteDisplayNumber(activeItem) : ""}`
                    : creating
                      ? "Neue Stückliste (Entwurf)"
                      : activeItem
                        ? `Stückliste ${stuecklisteDisplayNumber(activeItem)} (Entwurf)`
                        : "Stückliste (Entwurf)"}
                </h4>
                <div className="flex shrink-0 items-center gap-1">
                  {isFinal && canManage && activeItem ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void startRevision(activeItem)}
                      disabled={saving}
                    >
                      Bearbeiten
                    </Button>
                  ) : null}
                  {isFinal && activeItem?.public_view_url ? (
                    <button
                      type="button"
                      onClick={() => setPublicViewUrl(activeItem.public_view_url || null)}
                      className="px-2 text-xs font-medium text-accent hover:underline"
                    >
                      Digitale Ansicht
                    </button>
                  ) : null}
                  <Button type="button" variant="ghost" size="sm" onClick={resetEditor} disabled={saving}>
                    Schließen
                  </Button>
                </div>
              </div>

              {showTemplatePicker ? (
                <div className="space-y-3 rounded-ios border border-separator bg-fill-quaternary p-3">
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                      Aus bestehender Stückliste
                    </h5>
                    <p className="mt-1 text-xs text-label-tertiary">
                      Vorlage projektübergreifend suchen und in diesen Entwurf übernehmen.
                    </p>
                  </div>
                  <TextInput
                    label="Suche"
                    value={templateQuery}
                    onChange={(e) => setTemplateQuery(e.target.value)}
                    placeholder="Nummer, Bauteil, Auftrag, Kunde…"
                    disabled={cloningTemplate || saving}
                  />
                  {templateSearching ? (
                    <div className="flex items-center gap-2 text-xs text-label-secondary">
                      <Spinner className="h-3.5 w-3.5" />
                      Suche…
                    </div>
                  ) : null}
                  {templateQuery.trim() && !templateSearching && templateResults.length === 0 ? (
                    <p className="text-xs text-label-tertiary">Keine Stücklisten gefunden.</p>
                  ) : null}
                  {templateResults.length > 0 ? (
                    <ul className="max-h-48 space-y-1 overflow-y-auto rounded-ios border border-separator bg-card p-1">
                      {templateResults.map((row) => {
                        const selected = selectedTemplate?.id === row.id;
                        const label = stuecklisteDisplayNumber(row);
                        const meta = [
                          row.bauteil || "Ohne Bauteil",
                          row.auftrag_nummer || `Auftrag #${row.auftrag_id}`,
                          row.kunde_name || null,
                          `${row.position_count} Pos.`,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <li key={row.id}>
                            <button
                              type="button"
                              className={clsx(
                                "w-full rounded-ios px-2 py-1.5 text-left transition-colors",
                                selected
                                  ? "bg-accent-soft text-label"
                                  : "hover:bg-fill-tertiary text-label",
                              )}
                              onClick={() => setSelectedTemplate(row)}
                              disabled={cloningTemplate || saving}
                            >
                              <div className="text-sm font-medium">{label}</div>
                              <div className="text-xs text-label-secondary">{meta}</div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {selectedTemplate ? (
                    <div className="space-y-2">
                      <Checkbox
                        id="stueckliste-keep-order-links"
                        label="Bestellverknüpfungen übernehmen"
                        description={
                          canKeepOrderLinks
                            ? "Übernimmt TK-Verknüpfungen der Positionen (Status „Bestellt“)."
                            : "Nur im selben Projekt möglich."
                        }
                        checked={keepOrderLinks && canKeepOrderLinks}
                        disabled={!canKeepOrderLinks || cloningTemplate || saving}
                        onChange={(e) => setKeepOrderLinks(e.target.checked)}
                      />
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void applyTemplateClone()}
                          disabled={cloningTemplate || saving}
                        >
                          {cloningTemplate ? "Übernehme…" : "Übernehmen"}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput
                  label="Datum"
                  type="date"
                  value={datum}
                  onChange={(e) => setDatum(e.target.value)}
                  disabled={isFinal || !canManage}
                />
                <TextInput
                  label="Bauteil"
                  value={bauteil}
                  onChange={(e) => setBauteil(e.target.value)}
                  disabled={isFinal || !canManage}
                />
              </div>

              <div className="space-y-3 rounded-ios border border-separator bg-fill-quaternary p-3">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                  Beschichtung
                </h5>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectInput
                    label="Art"
                    value={beschichtungArt}
                    onChange={(e) => {
                      const next = e.target.value as BeschichtungArt | "";
                      setBeschichtungArt(next);
                      if (next !== "grundiert") setBeschichtungGrundiertTon("");
                      if (next !== "verzinkt") setBeschichtungFinish("");
                      if (
                        next !== "gepulvert" &&
                        next !== "lackiert" &&
                        next !== "verzinkt"
                      ) {
                        setBeschichtungRal("");
                      }
                    }}
                    disabled={isFinal || !canManage}
                  >
                    <option value="">— bitte wählen —</option>
                    {BESCHICHTUNG_ART_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectInput>

                  {beschichtungArt === "grundiert" ? (
                    <SelectInput
                      label="Grundierung"
                      value={beschichtungGrundiertTon}
                      onChange={(e) =>
                        setBeschichtungGrundiertTon(
                          e.target.value as BeschichtungGrundiertTon | "",
                        )
                      }
                      disabled={isFinal || !canManage}
                    >
                      <option value="">— hell / dunkel —</option>
                      <option value="hell">Hell</option>
                      <option value="dunkel">Dunkel</option>
                    </SelectInput>
                  ) : null}

                  {beschichtungArt === "verzinkt" ? (
                    <SelectInput
                      label="Zusätzlich"
                      value={beschichtungFinish}
                      onChange={(e) => {
                        const next = e.target.value as BeschichtungFinish | "";
                        setBeschichtungFinish(next);
                        if (!next) setBeschichtungRal("");
                      }}
                      disabled={isFinal || !canManage}
                    >
                      <option value="">Keine Nachbehandlung</option>
                      <option value="gepulvert">Gepulvert</option>
                      <option value="lackiert">Lackiert</option>
                    </SelectInput>
                  ) : null}

                  {beschichtungArt === "gepulvert" ||
                  beschichtungArt === "lackiert" ||
                  (beschichtungArt === "verzinkt" && !!beschichtungFinish) ? (
                    <TextInput
                      label="RAL-Ton"
                      value={beschichtungRal}
                      onChange={(e) => setBeschichtungRal(e.target.value)}
                      placeholder="z. B. 7016"
                      disabled={isFinal || !canManage}
                    />
                  ) : null}
                </div>
                {beschichtungArt ? (
                  <p className="text-xs text-label-tertiary">
                    Vorschau:{" "}
                    {formatBeschichtungDisplay({
                      beschichtung_art: beschichtungArt,
                      beschichtung_grundiert_ton: beschichtungGrundiertTon,
                      beschichtung_finish: beschichtungFinish,
                      beschichtung_ral: beschichtungRal,
                    }) || "—"}
                  </p>
                ) : null}
              </div>

              {!isFinal && canManage ? (
                availableTkGrouped.length > 0 ? (
                  <div className="rounded-ios border border-accent/30 bg-accent/5 p-3">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                          Bestellungen & Artikel übernehmen
                        </h5>
                        <p className="mt-0.5 text-xs text-label-tertiary">
                          Dieselben Positionen wie in der Projektübersicht – einzeln oder alle
                          übernehmen. Eine Bestellzeile kann mehrfach übernommen werden
                          (z.&nbsp;B. Zuschnitte).
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="secondary" onClick={adoptAllTkVorschlaege}>
                        Alle übernehmen
                      </Button>
                    </div>
                    <div className="max-h-64 space-y-2 overflow-y-auto">
                      {availableTkGrouped.map(([bestellnummer, lines]) => (
                        <div
                          key={bestellnummer}
                          className="rounded-md border border-separator bg-card px-2 py-2"
                        >
                          <p className="mb-1.5 text-xs font-semibold text-label">{bestellnummer}</p>
                          <ul className="space-y-1.5">
                            {lines.map((row) => {
                              const usedHere = linkedOrderLineIds.has(row.order_line_id);
                              return (
                              <li
                                key={row.order_line_id}
                                className="flex items-start gap-2 rounded-md border border-separator bg-fill-quaternary px-2 py-1.5 text-sm"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <p className="font-medium text-label">{row.text || "—"}</p>
                                    {usedHere ? (
                                      <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary">
                                        bereits übernommen
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="text-xs text-label-tertiary">
                                    {[
                                      row.menge != null
                                        ? `${row.menge}${row.einheit ? ` ${row.einheit}` : ""}`
                                        : null,
                                      row.tk_status_label ? `TK: ${row.tk_status_label}` : null,
                                      row.hero_nr ? `Hero ${row.hero_nr}` : null,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => adoptTkVorschlag(row)}
                                >
                                  {usedHere ? "Weitere übernehmen" : "Übernehmen"}
                                </Button>
                              </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-label-tertiary">
                    Keine offenen Bestellpositionen für dieses Projekt gefunden. Sie können
                    Positionen manuell hinzufügen.
                  </p>
                )
              ) : null}

              <div>
                <div className="sticky top-0 z-10 -mx-1 mb-2 flex items-center justify-between gap-2 bg-card/95 px-1 py-2 backdrop-blur-sm">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Positionen
                  </h5>
                  {!isFinal && canManage ? (
                    <Button type="button" size="sm" variant="secondary" onClick={openCreatePosition}>
                      Position hinzufügen
                    </Button>
                  ) : null}
                </div>
                {positionen.length ? (
                  <ul className="space-y-1.5">
                    {positionen.map((pos, idx) => {
                      const zuschnitt = getZuschnittBadgeParts(pos);
                      const canReorder = !isFinal && canManage;
                      const isDragging =
                        dragState?.kind === "standard" && dragState.from === idx;
                      const showDropBefore =
                        dropState?.kind === "standard" && dropState.index === idx;
                      return (
                        <li key={`std-${pos.id ?? "new"}-${idx}`}>
                          {showDropBefore ? (
                            <div className="mb-1 h-0.5 rounded-full bg-accent" aria-hidden />
                          ) : null}
                          <div
                            className={clsx(
                              "flex items-start gap-2 rounded-md border border-separator px-2 py-1.5 text-sm transition",
                              isDragging && "opacity-50",
                            )}
                            onContextMenu={(e) => openPositionContextMenu("standard", idx, e)}
                            onDragOver={(e) => {
                              if (!canReorder || dragState?.kind !== "standard") return;
                              e.preventDefault();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const before = e.clientY - rect.top < rect.height / 2;
                              setDropState({
                                kind: "standard",
                                index: resolveDropIndex(idx, before),
                              });
                            }}
                            onDrop={(e) => {
                              if (!canReorder || dragState?.kind !== "standard") return;
                              e.preventDefault();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const before = e.clientY - rect.top < rect.height / 2;
                              movePosition(
                                "standard",
                                dragState.from,
                                resolveDropIndex(idx, before),
                              );
                              clearDrag();
                            }}
                          >
                            {canReorder ? (
                              <button
                                type="button"
                                className="mt-0.5 shrink-0 cursor-grab px-0.5 text-label-tertiary active:cursor-grabbing"
                                title="Ziehen zum Sortieren"
                                aria-label={`Position ${idx + 1} verschieben`}
                                draggable
                                onDragStart={(e) => {
                                  setDragState({ kind: "standard", from: idx });
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/plain", String(idx));
                                }}
                                onDragEnd={clearDrag}
                              >
                                ⠿
                              </button>
                            ) : null}
                            <span className="w-6 shrink-0 text-label-tertiary">{idx + 1}.</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <p className="font-medium text-label">{pos.beschreibung || "—"}</p>
                                <span
                                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${positionStatusBadgeClass(pos.position_status)}`}
                                >
                                  {positionStatusLabel(pos.position_status)}
                                </span>
                                {zuschnitt ? (
                                  <span className="inline-flex flex-col rounded-md bg-status-orange/15 px-2 py-1 text-status-orange leading-tight">
                                    <span className="whitespace-nowrap text-sm font-extrabold">
                                      {zuschnitt.angles}
                                      {zuschnitt.lageSymbol ? (
                                        <span className="ml-1 font-mono text-base font-black tracking-wide">
                                          {zuschnitt.lageSymbol}
                                        </span>
                                      ) : null}
                                    </span>
                                    <span className="text-[10px] font-semibold">Zuschnitt</span>
                                  </span>
                                ) : null}
                                {pos.ist_montagematerial ? (
                                  <span className="rounded-full bg-status-blue/15 px-2 py-0.5 text-[11px] font-medium text-status-blue">
                                    Montagematerial
                                  </span>
                                ) : null}
                                {pos.position_status === "bestellt" && pos.tk_status_label ? (
                                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary">
                                    TK {pos.tk_status_label}
                                    {pos.bestellnummer ? ` · ${pos.bestellnummer}` : ""}
                                  </span>
                                ) : pos.bestellnummer ? (
                                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary">
                                    {pos.bestellnummer}
                                  </span>
                                ) : null}
                              </div>
                              <p className="text-xs text-label-tertiary">
                                {[
                                  pos.anzahl != null ? `Anz. ${pos.anzahl}` : null,
                                  pos.groesse ? `Größe ${pos.groesse}` : null,
                                  pos.hero_nr ? `Hero ${pos.hero_nr}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            {!isFinal && canManage ? (
                              <div className="flex shrink-0 gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditPosition(idx)}
                                >
                                  Bearbeiten
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePosition(idx)}
                                >
                                  Entfernen
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                    {dropState?.kind === "standard" &&
                    dropState.index === positionen.length ? (
                      <li>
                        <div className="h-0.5 rounded-full bg-accent" aria-hidden />
                      </li>
                    ) : null}
                    {dragState?.kind === "standard" ? (
                      <li
                        className="min-h-2"
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDropState({ kind: "standard", index: positionen.length });
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          movePosition("standard", dragState.from, positionen.length);
                          clearDrag();
                        }}
                      />
                    ) : null}
                  </ul>
                ) : (
                  <p className="text-sm text-label-tertiary">Noch keine Positionen.</p>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    Besonderes Werkzeug
                  </h5>
                  {!isFinal && canManage ? (
                    <Button type="button" size="sm" variant="secondary" onClick={openCreateWerkzeug}>
                      Werkzeug hinzufügen
                    </Button>
                  ) : null}
                </div>
                {werkzeugPositionen.length ? (
                  <ul className="space-y-1.5">
                    {werkzeugPositionen.map((pos, idx) => {
                      const canReorder = !isFinal && canManage;
                      const isDragging =
                        dragState?.kind === "werkzeug" && dragState.from === idx;
                      const showDropBefore =
                        dropState?.kind === "werkzeug" && dropState.index === idx;
                      return (
                        <li key={`wz-${pos.id ?? "new"}-${idx}`}>
                          {showDropBefore ? (
                            <div className="mb-1 h-0.5 rounded-full bg-accent" aria-hidden />
                          ) : null}
                          <div
                            className={clsx(
                              "flex items-start gap-2 rounded-md border border-separator px-2 py-1.5 text-sm transition",
                              isDragging && "opacity-50",
                            )}
                            onContextMenu={(e) => openPositionContextMenu("werkzeug", idx, e)}
                            onDragOver={(e) => {
                              if (!canReorder || dragState?.kind !== "werkzeug") return;
                              e.preventDefault();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const before = e.clientY - rect.top < rect.height / 2;
                              setDropState({
                                kind: "werkzeug",
                                index: resolveDropIndex(idx, before),
                              });
                            }}
                            onDrop={(e) => {
                              if (!canReorder || dragState?.kind !== "werkzeug") return;
                              e.preventDefault();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const before = e.clientY - rect.top < rect.height / 2;
                              movePosition(
                                "werkzeug",
                                dragState.from,
                                resolveDropIndex(idx, before),
                              );
                              clearDrag();
                            }}
                          >
                            {canReorder ? (
                              <button
                                type="button"
                                className="mt-0.5 shrink-0 cursor-grab px-0.5 text-label-tertiary active:cursor-grabbing"
                                title="Ziehen zum Sortieren"
                                aria-label={`Werkzeugposition ${idx + 1} verschieben`}
                                draggable
                                onDragStart={(e) => {
                                  setDragState({ kind: "werkzeug", from: idx });
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/plain", String(idx));
                                }}
                                onDragEnd={clearDrag}
                              >
                                ⠿
                              </button>
                            ) : null}
                            <span className="w-6 shrink-0 text-label-tertiary">{idx + 1}.</span>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-label">{pos.beschreibung || "—"}</p>
                              <p className="text-xs text-label-tertiary">
                                {[
                                  pos.anzahl != null ? `Anz. ${pos.anzahl}` : null,
                                  pos.bemerkung || null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            {!isFinal && canManage ? (
                              <div className="flex shrink-0 gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditWerkzeug(idx)}
                                >
                                  Bearbeiten
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeWerkzeugPosition(idx)}
                                >
                                  Entfernen
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                    {dropState?.kind === "werkzeug" &&
                    dropState.index === werkzeugPositionen.length ? (
                      <li>
                        <div className="h-0.5 rounded-full bg-accent" aria-hidden />
                      </li>
                    ) : null}
                    {dragState?.kind === "werkzeug" ? (
                      <li
                        className="min-h-2"
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDropState({
                            kind: "werkzeug",
                            index: werkzeugPositionen.length,
                          });
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          movePosition("werkzeug", dragState.from, werkzeugPositionen.length);
                          clearDrag();
                        }}
                      />
                    ) : null}
                  </ul>
                ) : (
                  <p className="text-sm text-label-tertiary">
                    Noch kein besonderes Werkzeug (z. B. Steiger, Staubschutzvorhang).
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                  Notizen
                </h5>
                <TextareaInput
                  value={notizen}
                  onChange={(e) => setNotizen(e.target.value)}
                  rows={3}
                  placeholder="Hinweise für Werkstatt und Montage …"
                  disabled={isFinal || !canManage}
                />
              </div>

              {error ? <Alert variant="error">{error}</Alert> : null}

              {!isFinal && canManage ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <DocumentSaveStatus
                    saveState={saveState}
                    hasUnsavedChanges={hasUnsavedChanges}
                    lastSavedAt={lastSavedAt}
                    saveError={saveError}
                    onRetry={() => void retrySave()}
                    retrying={saveState === "saving"}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    {activeItem ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        disabled={saving || saveState === "saving"}
                        onClick={() => void handleDelete(activeItem)}
                      >
                        Entwurf löschen
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      loading={saving || saveState === "saving"}
                      disabled={!positionen.length || saveState === "pending"}
                      onClick={() => void requestFinalize()}
                    >
                      Fertigstellen
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {error && !editorOpen ? <Alert variant="error">{error}</Alert> : null}
        </>
      )}

      <StuecklistePositionDialog
        open={positionDialogOpen}
        orderId={orderId}
        editPosition={editPositionIndex != null ? positionen[editPositionIndex] ?? null : null}
        onClose={() => {
          setPositionDialogOpen(false);
          setEditPositionIndex(null);
        }}
        onSave={savePosition}
      />

      <StuecklisteWerkzeugDialog
        open={werkzeugDialogOpen}
        editPosition={
          editWerkzeugIndex != null ? werkzeugPositionen[editWerkzeugIndex] ?? null : null
        }
        onClose={() => {
          setWerkzeugDialogOpen(false);
          setEditWerkzeugIndex(null);
        }}
        onSave={saveWerkzeugPosition}
      />

      <ConfirmActionDialog
        open={finalizeConfirmOpen}
        title="Stückliste fertigstellen?"
        confirmLabel="Fertigstellen"
        cancelLabel="Abbrechen"
        variant="warning"
        loading={saving}
        zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
        onCancel={() => setFinalizeConfirmOpen(false)}
        onConfirm={() => void handleFinalizeConfirmed()}
      >
        Es wird eine PDF mit Nummer SL… erzeugt und im Ordner Montageunterlagen in SharePoint
        abgelegt. Danach kann die Stückliste nicht mehr bearbeitet werden.
      </ConfirmActionDialog>

      <StuecklistePublicViewDialog
        open={Boolean(publicViewUrl)}
        url={publicViewUrl}
        onClose={() => setPublicViewUrl(null)}
      />

      <ContextMenu
        state={contextMenu}
        onClose={() => setContextMenu(null)}
      />
    </div>
  );
}
